import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../config/apiClientConfig';

const getAdminAppInitAPI = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/admin/app/init',
		responseSchema: z.object({
			insuranceCompanies: z.array(
				z.object({
					id: z.number(),
					name: z.string(),
					shortName: z.string(),
					logoUrl: z.string(),
					websiteUrl: z.string(),
					enableQuotes: z.boolean(),
					enablePolicy: z.boolean(),
					isActive: z.boolean(),
					companyId: z.number(),
				})
			),
			genders: z.array(z.object({ id: z.number(), name: z.string() })),
			countries: z.array(
				z.object({ id: z.number(), name: z.string(), nationality: z.string().nullish(), isGCC: z.boolean() })
			),
			benefitCategories: z.array(z.object({ id: z.number(), name: z.string(), shortName: z.string() })),
			benefits: z.array(
				z.object({
					id: z.number(),
					code: z.string(),
					benefitCategoryId: z.number(),
					name: z.string(),
					description: z.string(),
				})
			),
			relationships: z.array(z.object({ id: z.number(), name: z.string() })),
			facilityTypes: z.array(z.object({ id: z.number(), name: z.string() })),
			planExclusions: z.array(z.object({ id: z.number(), description: z.string() })),
			planTypes: z.array(z.object({ id: z.number(), name: z.string() })),
			policyTypes: z.array(z.object({ id: z.number(), type: z.string() })),
			channelTypes: z.array(z.object({ id: z.number(), name: z.string() })),
			quoteStatuses: z.array(z.object({ id: z.number(), name: z.string() })),
			plans: z.array(
				z.object({
					id: z.number(),
					name: z.string(),
					topHospitalList: z.string(),
					code: z.string(),
					insuranceCompanyId: z.number(),
					planTypeIds: z.array(z.number()),
					annualLimitCover: z.string(),
					annualLimitPharmacy: z.string(),
					coPaymentsDiagnostic: z.string(),
					coPaymentsPharmacy: z.string(),
					coPaymentsLabTests: z.string(),
					coPaymentsOPConsultations: z.string(),
					coPaymentsPhysioTreatment: z.string(),
					coPaymentsMaternity: z.string(),
					coPaymentsDental: z.string(),
					coPaymentsOptical: z.string(),
					policyWordingUrlEn: z.string(),
					policyWordingUrlAr: z.string(),
					planKeyBenefits: z.array(z.object({ benefitCategoryId: z.number(), isKeyBenefit: z.boolean() })),
					planNetworkCounts: z.array(z.object({ facilityTypeId: z.number(), count: z.number(), display: z.boolean() })),
					networkGroupId: z.number(),
					benefitSummary: z.array(z.string()),
				})
			),
			paymentVendors: z.array(z.object({ id: z.number(), label: z.string() })),
			cardTypes: z.array(z.object({ id: z.number(), label: z.string() })),
			paymentMethod: z.array(z.object({ id: z.number(), label: z.string() })),
			paymentPricingTypes: z.array(z.object({ id: z.number(), label: z.string() })),
			transactionTypes: z.array(z.object({ id: z.number(), label: z.string() })),
			policyStatus: z.array(z.object({ id: z.number(), name: z.string() })),
			emirates: z.array(z.object({ id: z.number(), name: z.string(), isDisabled: z.boolean() })),
			salaryBands: z.array(z.object({ id: z.number(), name: z.string() })),
			networkGroups: z.array(z.object({ id: z.number(), name: z.string(), code: z.string() })),
			cities: z.array(z.object({ id: z.number(), name: z.string() })),
			insuranceCardStatuses: z.array(z.object({ id: z.number(), type: z.string() })),
			sanctionScreeningStatuses: z.array(z.object({ id: z.number(), type: z.string() })),
			uploadedDocumentConfigs: z
				.array(
					z.object({
						uploadedDocumentType: z.string(),
						allowedTypes: z.string(),
						maxAllowedSizeInKb: z.number(),
					})
				)
				.optional(),
		}),
	});

export type AdminAppInitResponse = TypeOfAPIResponse<ReturnType<typeof getAdminAppInitAPI>>;

export type AdminAppInitError = TypeOfAPIError<ReturnType<typeof getAdminAppInitAPI>>;

export default getAdminAppInitAPI;
